import clsx from "clsx";
import mixpanel from "mixpanel-browser";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useToastsContext } from "../../contexts/toasts";
import {
  CreateRefreshToken,
  HandleApiSignOut,
} from "../../graphql/auth/auth.mutations";
import useIsMobile from "../../hooks/useIsMobile";
import useStores from "../../hooks/useStores";
import useUserAuditLogs from "../../hooks/useUserAuditLogs";
import Button from "../Button";
import "./integrations.css";
const OneDrive = "/imgs/blocks/onedrive.png";

type Details = {
  name: string;
  email: string;
};

function OneDriveIntegration() {
  const { auth } = useStores();

  const { addToast } = useToastsContext();

  const params = useParams();
  const [searchParams] = useSearchParams();

  const isMobile = useIsMobile();

  const { createUserAuditLog } = useUserAuditLogs();

  const redirectFrom = params.redirectFrom;
  const code = searchParams.get("code");

  const [details, setDetails] = useState<Details | null>(null);
  const loadIntegrationDetails = useCallback(() => {
    const integrations = auth.user?.integrations;
    if (integrations && integrations["one-drive"]) {
      setDetails({
        name: integrations["one-drive"].name,
        email: integrations["one-drive"].email,
      });
    }
  }, []);

  useEffect(() => {
    if (!auth.user || !auth.user.id) {
      return;
    }
    if (code && redirectFrom === "one-drive") {
      auth.apolloClient
        .mutate({
          mutation: CreateRefreshToken,
          variables: {
            tokenInput: {
              code,
              userId: auth.user.id,
              type: "one-drive",
            },
          },
        })
        .then((res: any) => {
          console.log(res);
          if (res && res.data && res.data.createRefreshToken) {
            // addToast("One Drive Enabled.", {
            //   type: "success",
            // });
            window.location.href = "/settings/profile";
          } else {
            addToast("  Something went wrong. Please try again later.", {
              type: "error",
            });
          }
        });
    } else {
      loadIntegrationDetails();
    }
  }, [code, redirectFrom, auth]);

  async function handleSignIn() {
    if (auth && auth.user && auth.user.id) {
      // Add to mixpanel
      mixpanel.track("one-drive-integration-clicked");

      createUserAuditLog({
        actionId: "one-drive-integration-clicked",
        actionType: "MISC",
        details: {},
      });

      const clientId = "55864141-cda0-498a-897a-57677e16d372";
      const scope = "files.readwrite offline_access User.Read";
      const redirectUri = `${
        import.meta.env.MODE === "development"
          ? "http://localhost:8081"
          : import.meta.env.VITE_APP_URL
      }/settings/profile/one-drive`;
      const url =
        "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=" +
        clientId +
        "&scope=" +
        scope +
        "&response_type=code&redirect_uri=" +
        redirectUri;
      window.location.href = url;
    }
  }

  const handleSignOut = async () => {
    if (auth && auth.user && auth.user.id) {
      const userId: string = auth.user.id;
      auth.apolloClient
        .mutate({
          mutation: HandleApiSignOut,
          variables: {
            tokenInput: {
              userId,
              type: "one-drive",
            },
          },
        })
        .then((res: any) => {
          if (res && res.data && res.data.handleApiSignOut) {
            addToast("OneDrive Disabled.", {
              type: "success",
            });
            auth.fetch().then(() => {
              loadIntegrationDetails();
            });
          } else {
            addToast("Something went wrong. Please try again later.", {
              type: "error",
            });
          }
        });
    }
  };

  return (
    <div className="border-gray-200 w-full rounded-xl border p-5">
      <div
        className={clsx(
          isMobile ? "flex-col" : "flex",
          "w-full items-center justify-between"
        )}
      >
        {/* Left side: Logo, title, and connection info */}
        <div className="flex items-center">
          <img
            loading="lazy"
            src={OneDrive}
            className="h-6 w-6 object-contain"
          />
          <div className="pl-3">
            <div className="text-gray-700 text-[15px] font-medium">
              Microsoft OneDrive
            </div>
            {details && (
              <div className="mt-1">
                {details.name && (
                  <div className="text-gray-700 text-[14px] font-semibold">
                    {details.name}
                  </div>
                )}
                {details.email && (
                  <div className="text-gray-500 text-[13px]">
                    {details.email}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right side: Action button */}
        <div className={clsx(isMobile && "mt-4 w-full")}>
          {details ? (
            <Button
              theme="secondary"
              type="button"
              buttonText="Sign out"
              padding="large"
              width="225"
              onClick={handleSignOut}
              trackingName="button-clicked-onedrive-integration-sign-out"
            />
          ) : (
            <button
              onClick={() => handleSignIn()}
              className="inline-flex w-[225px] justify-center rounded-lg border py-2 px-4 pl-6 text-sm shadow-sm"
            >
              <img
                loading="lazy"
                className="my-1 h-4"
                src={"/imgs/logos/microsoftLogo.png"}
                alt="Microsoft Logo"
              />
              <span className="ml-[12px] w-40 pt-[1px] font-[15px]">
                Sign in with Microsoft
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default observer(OneDriveIntegration);
